import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Mail, MailCheck } from "lucide-react";
import PlayfulNavbar from "../components/playful/PlayfulNavbar";
import PlayfulFooter from "../components/playful/PlayfulFooter";
import { CandyButton } from "../components/playful/CandyButton";
import { PopInput } from "../components/playful/PopInput";
import { StickerCard } from "../components/playful/StickerCard";
import { GeometricBadge } from "../components/playful/GeometricBadge";
import { FloatingIcon } from "../components/playful/FloatingIcon";
import { Blob } from "../components/playful/Decor";
import Turnstile, { TURNSTILE_ENABLED } from "../components/playful/Turnstile";
import { api, formatApiError } from "../lib/api";

export default function Forgot() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [captchaToken, setCaptchaToken] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    if (TURNSTILE_ENABLED && !captchaToken) {
      toast.error("Please complete the bot-protection check.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", {
        email: email.trim().toLowerCase(),
        captcha_token: captchaToken || undefined,
      });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen overflow-x-hidden bg-paper text-ink">
      <PlayfulNavbar />
      <section className="relative mx-auto max-w-[1400px] px-5 pb-32 pt-16 md:px-10">
        <Blob
          tone="pink"
          size={300}
          className="absolute -right-20 -top-20 animate-drift opacity-70"
        />
        <Blob
          tone="yellow"
          size={200}
          className="absolute left-4 top-40 animate-float opacity-70"
        />

        <div className="relative grid grid-cols-12 gap-10">
          <div className="col-span-12 md:col-span-7">
            <GeometricBadge tone="yellow">PASSWORD RESET</GeometricBadge>
            <h1 className="mt-4 font-display text-5xl font-extrabold leading-[0.95] tracking-tight md:text-7xl">
              Forgot your password?
              <span className="block text-candy">We'll send a new lane.</span>
            </h1>
            <p className="mt-6 max-w-md font-sans text-xl">
              Enter your email and LinkLane will issue a reset link. Quiet, instant, no fanfare.
            </p>
            <div className="mt-8 flex gap-4">
              <FloatingIcon icon={sent ? MailCheck : Mail} tone="candy" size={60} rotate={-8} />
            </div>
          </div>
          <div className="col-span-12 md:col-span-5">
            <StickerCard
              tone={sent ? "mint" : "candy"}
              bg="paper"
              hover={false}
              className="p-8 md:p-10"
            >
              {sent ? (
                <div className="flex flex-col gap-5" data-testid="forgot-sent-state">
                  <GeometricBadge tone="mint">CHECK YOUR INBOX</GeometricBadge>
                  <p className="font-display text-2xl font-extrabold leading-snug">
                    Reset link sent to
                    <br />
                    <span className="break-all text-candy">{email}</span>
                  </p>
                  <p className="font-sans text-sm leading-relaxed text-muted-foreground">
                    Check your inbox (and spam folder). The link expires in{" "}
                    <strong>30 minutes</strong> and can only be used once.
                  </p>
                  <button
                    type="button"
                    onClick={() => setSent(false)}
                    className="text-left font-mono text-[11px] uppercase tracking-[0.22em] text-candy underline-offset-4 hover:underline"
                  >
                    Use a different email →
                  </button>
                  <div className="pt-1">
                    <Link
                      to="/login"
                      className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground underline-offset-4 hover:underline"
                    >
                      BACK TO LOGIN
                    </Link>
                  </div>
                </div>
              ) : (
                <form onSubmit={onSubmit} className="flex flex-col gap-6" data-testid="forgot-form">
                  <PopInput
                    type="email"
                    required
                    label="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@linklane.app"
                    autoFocus
                    data-testid="forgot-email-input"
                  />
                  <Turnstile onToken={setCaptchaToken} />
                  <CandyButton
                    type="submit"
                    variant="candy"
                    size="lg"
                    block
                    disabled={busy}
                    withArrow
                    data-testid="forgot-submit-btn"
                  >
                    {busy ? "Sending…" : "Send reset link"}
                  </CandyButton>
                  <div className="text-center font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
                    <Link to="/login" className="text-candy underline-offset-4 hover:underline">
                      BACK TO LOGIN
                    </Link>
                  </div>
                </form>
              )}
            </StickerCard>
          </div>
        </div>
      </section>
      <PlayfulFooter />
    </div>
  );
}
